import { Box, ListItemText, Stack, Typography } from "@mui/material";

import CustomButton from "../CustomButton";

import { getSelectedDrawersData } from "../../redux/selectors/drawerSelectors";
import { useSelector } from "react-redux";
import { getSiteOperationTempData } from "../../redux/selectors/siteSelectors";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import {
  useGetSiteConfigurationsListQuery,
  useRequestNewSiteConfigurationMutation
} from "../../redux/api/site";
import { skipToken } from "@reduxjs/toolkit/query";
import { useCallback } from "react";
import { uniqueBy } from "../../lib/siteHelpers";

type RequestNewConfigurationContentProps = {
  onClose: () => void;
};

const RequestNewConfiguration = (
  props: RequestNewConfigurationContentProps
) => {
  const { onClose } = props;
  const selectedDrawersData = useSelector(getSelectedDrawersData);
  const siteId = selectedDrawersData.site?.id;

  const { configurationType: configurationTypeToCopyFrom } = useSelector(
    getSiteOperationTempData
  ).requestNewSiteConfigurationTempData;

  const { data: currentSiteConfigurations } = useGetSiteConfigurationsListQuery(
    siteId
      ? {
          siteId
        }
      : skipToken
  );

  const [requestNewConfiguration, { isLoading }] =
    useRequestNewSiteConfigurationMutation();

  const onSubmit = async () => {
    if (!siteId || !configurationTypeToCopyFrom.id) return;
    try {
      await requestNewConfiguration({
        siteId,
        configurationTypeId: configurationTypeToCopyFrom.id
      }).unwrap();
    } catch (error) {
      console.error("Error while creating new configuration", error);
    }
  };
  const searchForParents = useCallback(
    (
      configTypeId: string,
      allConfigurationsMenuTypes: SiteConfigurationMenuGroupings
    ): {
      configurationTypeName: string;
      configurationTypeId: string;
      displayOrder: number;
      parentTypeId: string | null;
      onePerEnvironment: boolean;
    }[] => {
      const parent = allConfigurationsMenuTypes[configTypeId];

      if (!parent) {
        return [];
      }

      if (parent.parentTypeId) {
        return [
          ...searchForParents(parent.parentTypeId, allConfigurationsMenuTypes),
          parent
        ];
      }

      return [parent];
    },
    []
  );
  const currentConfigurationTypeParentStructure = searchForParents(
    configurationTypeToCopyFrom.id,
    currentSiteConfigurations?.allConfigurationsMenuTypes || {}
  );
  const uniqueChildrenConfigTypeToBeCopied = configurationTypeToCopyFrom
    .subConfigurations?.length
    ? uniqueBy(
        configurationTypeToCopyFrom.subConfigurations,
        (item) => item.configurationTypeId
      )
    : [];

  return (
    <Stack
      flexDirection={"column"}
      gap={1}
      justifyContent="space-between"
      alignItems={"center"}
      px={1}
      py={3}
      width={"100%"}
    >
      <Typography
        variant="subtitle1"
        sx={{
          whiteSpace: "wrap",
          textWrap: "pretty",
          lineHeight: 1.5
        }}
        aria-label="request new configuration description"
      >
        This configuration includes all nested items that will be created.
        <br />
        {"Parent configuration(s) are shown for context only."}
      </Typography>
      <Stack
        flexDirection={"column"}
        gap={0.5}
        alignItems={"center"}
        width={"70%"}
      >
        {currentConfigurationTypeParentStructure.map((configurationType) => {
          const { configurationTypeId, parentTypeId } = configurationType;
          const possibleparentDetails =
            currentSiteConfigurations?.allConfigurationsMenuTypes[
              parentTypeId || ""
            ];
          if (!possibleparentDetails) return <></>;

          return (
            <Stack
              flexDirection={"column"}
              gap={0.5}
              key={configurationTypeId}
              alignItems={"center"}
              width={"100%"}
              justifyContent={"center"}
            >
              <ListItemText
                primary={
                  <Typography variant="subtitle2" fontWeight={500}>
                    {possibleparentDetails?.configurationTypeName}
                  </Typography>
                }
                sx={{
                  border: (theme) => `1px solid ${theme.palette.grey[300]}`,
                  borderRadius: "8px",
                  width: "100%",
                  boxShadow: "0px 1px 4px 0px #00000040",
                  p: 2,
                  textAlign: "center"
                }}
              />
              <ArrowDownwardIcon />
            </Stack>
          );
        })}
        <Stack
          flexDirection={"column"}
          gap={1}
          alignItems={"center"}
          sx={{
            border: (theme) => `1px solid ${theme.palette.grey[300]}`,
            borderRadius: "8px",
            boxShadow: "0px 1px 4px 0px #00000040",
            px: 2,
            py: 1
          }}
          width={"100%"}
          mb={3}
        >
          <Box aria-label="new configuration to copy from description">
            <Typography
              variant="subtitle2"
              fontWeight={500}
              color="text.secondary"
            >
              Current configuration to create:
            </Typography>
            <Typography variant="subtitle1" fontWeight={600}>
              {configurationTypeToCopyFrom.name}
            </Typography>
          </Box>
          {currentSiteConfigurations &&
            !!uniqueChildrenConfigTypeToBeCopied.length && (
              <Stack
                flexDirection={"column"}
                gap={0.5}
                alignItems={"center"}
                aria-label="additional new subconfigurations to copy from description"
              >
                {uniqueChildrenConfigTypeToBeCopied.length && (
                  <ArrowDownwardIcon />
                )}
                <Typography
                  variant="subtitle2"
                  fontWeight={500}
                  color="text.secondary"
                >
                  {`Additional sub configuration(s) to be copied with new configuration: `}
                </Typography>
                {uniqueChildrenConfigTypeToBeCopied.map((configurationType) => {
                  const { configurationTypeId } = configurationType;
                  const generalConfigType =
                    currentSiteConfigurations.allConfigurationsMenuTypes[
                      configurationTypeId
                    ];
                  if (!generalConfigType) return <></>;
                  return (
                    <ListItemText
                      key={configurationTypeId}
                      primary={
                        <Typography variant="subtitle2" fontWeight={500}>
                          {generalConfigType?.configurationTypeName}
                        </Typography>
                      }
                    />
                  );
                })}
              </Stack>
            )}
        </Stack>
      </Stack>
      <Stack flexDirection="row" gap={3}>
        <CustomButton
          sx={{ height: 35 }}
          color="secondary"
          variant="contained"
          onClick={onClose}
          disabled={isLoading}
        >
          Close
        </CustomButton>
        <CustomButton
          sx={{ minWidth: 100, height: 35 }}
          variant="contained"
          onClick={() => {
            void onSubmit();
          }}
          disabled={isLoading}
          loading={isLoading}
        >
          Create new configuration
        </CustomButton>
      </Stack>
    </Stack>
  );
};
export default RequestNewConfiguration;
